import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State } from '@stencil/core';

import { hasSlotContent } from '../../../../../services/utils';

/**
 * @part title-container - Wrapper `<div> container that hold all the title elements inside
 * @part title-left - Wrapper `<div> container that hold the content on the left side
 * @part title-right - Wrapper `<div> container that hold Element on the right side
 * @part title-right-text - Wrapper `<div> container that hold the text content on the right side
 * @part title-right-image - Wrapper `<div> container that hold the content on the right side for image/buttons elements
 * @part down-icon - Pk-icon component for the chevron down icon on the right
 * @part up-icon - Pk-icon component for the chevron up icon on the right
 */
@Component({
  tag: 'pk-accordion-item-title',
  styleUrl: './pk-accordion-item-title.scss',
  shadow: true,
})
export class PkAccordionItemTitle {
  // Own Properties
  // ====================

  private titleRightRef: HTMLSpanElement;

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkAccordionItemTitleElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  @State() itemIndex: number;
  @State() titleRightSlotEmpty: boolean;

  // Public Property API
  // ========================

  /** If `true` the chevron icon up/down won't be shown at the right position of the title */
  @Prop({ reflect: true }) hideIcon: boolean = false;

  /** Position of the icon, right is default (right/left) */
  @Prop({ reflect: true }) iconPosition: string = 'right';

  /** Display arrow inside a circle */
  @Prop({ reflect: true }) circleIcon: boolean = false;

  /** Item is disabled */
  @Prop({ reflect: true }) disabled: boolean = false;

  /** Default expand state */
  @Prop({ reflect: true }) expanded: boolean = false;

  /** Set color for text (only tokens accepted) */
  @Prop({ reflect: true }) textColor: string = 'text-01';

  /** Set color for icon (only tokens accepted) */
  @Prop({ reflect: true }) iconColor: string = 'text-02';

  // Prop lifecycle events
  // =======================

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  /** Callback handler to be called when the Accordion Title has been clicked */
  @Event() pkClick: EventEmitter;

  /** Callback handler to be called when the Accordion Title has been clicked
   *  and the Accordion toggle event should be triggered */
  @Event() pkTriggerToggle: EventEmitter;

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  componentDidLoad() {
    this.handleSlotChange();
    this.el.setAttribute('role', 'heading');
  }

  // Listeners
  // ==============

  @Listen('click')
  handleClick(ev: Event) {
    const path = ev.composedPath();
    // Toggle the Accordion only if the event target is included in the path (it is inside the host element)
    if (!path.includes(ev.target)) return;

    this.toggleAccordion(ev);
  }

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  /** Set index of collapse item in parent (used by parent component) */
  @Method()
  setIndex(value: number) {
    this.itemIndex = value;
    return Promise.resolve();
  }

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  /**
   * This function is called when the user clicks on the header of the accordion, and the accordion is opening, closing
   */
  private toggleAccordion = (ev: Event) => {
    if (this.disabled) return;

    const _ev = this.pkClick.emit(ev.target);
    if (!_ev.defaultPrevented) {
      this.pkTriggerToggle.emit();
    }
  };

  private handleSlotChange = () => {
    if (!this.titleRightRef) return;

    this.titleRightSlotEmpty = !hasSlotContent(this.titleRightRef, 'title-right-text');
  };

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    const iconName = this.expanded ? 'up' : 'down';
    const style = {
      ...(this.textColor && { '--accordion--title-color': `var(${this.textColor})` }),
      ...(this.iconColor && { '--accordion--icon-color': `var(${this.iconColor})` }),
    };

    return (
      <Host
        class={{
          'title-host': true,
          'is-disabled': this.disabled,
          'is-expanded': this.expanded,
        }}
        style={{ ...style }}
        aria-level={this.itemIndex + 1}
        aria-controls={`accordion-content-${this.itemIndex}`}
      >
        <button
          class={{
            'title-container py-s': true,
            'is-disabled': this.disabled,
            'reverse-row': this.iconPosition === 'left',
          }}
          aria-expanded={this.expanded ? 'true' : 'false'}
          part="title-container"
          type="button"
        >
          <div class="title">
            <div class="title__left" part="title-left">
              <slot />
            </div>
            <div class="title__right flex items-center" part="title-right">
              <span
                class={{
                  text: true,
                  hidden: !this.expanded || this.titleRightSlotEmpty,
                }}
                ref={(el) => (this.titleRightRef = el)}
                part="title-right-text"
              >
                <slot name="title-right-text" onSlotchange={this.handleSlotChange} />
              </span>
              <slot name="title-right-image" />
            </div>
          </div>
          <pk-icon
            class={{
              'transition-transform': true,
              'rotate-180': this.expanded,
              '!hidden': this.hideIcon,
            }}
            aria-hidden="true"
            name={this.circleIcon ? 'navigation-chevron-down-circle' : 'navigation-chevron-down'}
            color={this.iconColor}
            part={`${iconName}-icon icon"`}
          />
        </button>
      </Host>
    );
  }
}
